/**
 * The explorer's shared action model. Every menu the explorer shows - the
 * per-row context menu, the per-row "more" (three-dots) menu, the
 * folder/tree context menu, the empty-space pane menu and the toolbar's
 * "Actions" dropdown - is built from `RowAction`s produced by the page
 * (PartnerFilesPage, StaffSharingFilesTab) from one place per row/folder,
 * given the caller's role, the current folder and the item's status.
 * Nothing computes "can this be downloaded/deleted" a second, different
 * way, so the menus cannot offer an action the server would refuse.
 *
 * `resetPassword` and `remove` are the members table's own action types
 * (utils/memberActions.ts, a sibling model built on this same `RowAction`
 * contract so its per-row menu and its toolbar "Actions" dropdown cannot
 * drift apart either, same as files and folders here). `close` is the
 * sharings table's own (utils/sharingActions.ts).
 */
export type ActionType =
  | "download"
  | "delete"
  | "copyName"
  | "open"
  | "uploadHere"
  | "refresh"
  | "newFolder"
  | "deleteFolder"
  | "resetPassword"
  | "remove"
  | "close";

export interface RowAction {
  key: string;
  type: ActionType;
  label: string;
  onSelect: () => void;
}

/** "Copy file name" is offered on every file row, regardless of role or status. */
export function copyNameAction(name: string): RowAction {
  return {
    key: "copy-name",
    type: "copyName",
    label: "Copy file name",
    onSelect: () => {
      void navigator.clipboard?.writeText(name);
    },
  };
}

export function downloadAction(onSelect: () => void): RowAction {
  return { key: "download", type: "download", label: "Download", onSelect };
}

export function deleteAction(onSelect: () => void): RowAction {
  return { key: "delete", type: "delete", label: "Delete", onSelect };
}

export function openAction(onSelect: () => void): RowAction {
  return { key: "open", type: "open", label: "Open", onSelect };
}

export function uploadHereAction(onSelect: () => void, label = "Upload here"): RowAction {
  return { key: "upload-here", type: "uploadHere", label, onSelect };
}

export function refreshAction(onSelect: () => void): RowAction {
  return { key: "refresh", type: "refresh", label: "Refresh", onSelect };
}

/** ADR 0010: distinct from "delete" so "Delete selected" (files) never touches a folder row. */
export function newFolderAction(onSelect: () => void): RowAction {
  return { key: "new-folder", type: "newFolder", label: "New folder", onSelect };
}

export function deleteFolderAction(onSelect: () => void): RowAction {
  return { key: "delete-folder", type: "deleteFolder", label: "Delete folder", onSelect };
}

/** Whether any row in `rows` offers an action of this type (drives the toolbar's Actions dropdown). */
export function anyRowHasAction(rows: readonly { actions?: RowAction[] }[], type: ActionType): boolean {
  return rows.some((row) => row.actions?.some((action) => action.type === type));
}

/** Runs every selected row's action of this type, for example "Download selected" or "Delete selected". */
export function runSelectedActions<T extends { key: string; actions?: RowAction[] }>(
  rows: readonly T[],
  selectedKeys: ReadonlySet<string>,
  type: ActionType,
): void {
  for (const row of rows) {
    if (!selectedKeys.has(row.key)) {
      continue;
    }
    const action = row.actions?.find((candidate) => candidate.type === type);
    action?.onSelect();
  }
}
